/**
 * 命令执行器：编排一轮对话的命令队列。
 * 流程：cancel 旧 turn → enqueue → pump（前置条件等待 → 派发 → 超时 → 回执上报 →
 *       后端 Verifier 响应 → 重试/降级/继续）→ planFinished。
 */
import { AgentCommandQueue } from "@/agent/AgentCommandQueue";
import type { AgentCapabilityRegistry } from "@/agent/AgentCapabilityRegistry";
import type { AgentCommandReporter } from "@/agent/AgentCommandReporter";
import type {
  AgentUiCommand,
  AgentV2ChatResponse,
  CommandReceipt,
  CommandResultResponse,
  QueueEvent
} from "@/agent/types";
import { AgentConstants } from "@/agent/agentConstants";

export interface AgentExecutorDeps {
  registry: AgentCapabilityRegistry;
  queue: AgentCommandQueue;
  reporter: AgentCommandReporter;
  /** 派发命令到对应页面 handler（App.vue 在 M10 注入；测试注入 fake） */
  dispatch: (command: AgentUiCommand, signal: AbortSignal) => Promise<CommandReceipt>;
  /** 本轮全部命令终结后的回调（widget 显示 answerPatch / planStatus） */
  onTurnFinished?: (info: {
    sessionId: string;
    turnId: string;
    planId: string;
    planStatus: string;
    answerPatch?: string;
  }) => void;
}

type ExecutorListener = (event: QueueEvent) => void;

const PRECONDITION_TIMEOUT_MS = 12000;
const RECEIPT_REPORT_ATTEMPTS = 3;

export class AgentCommandExecutor {
  private deps: AgentExecutorDeps;
  private sessionId = "";
  private turnId = "";
  private planId = "";
  private abortController: AbortController | null = null;
  private pumping = false;
  private pendingResponse: AgentV2ChatResponse | null = null;
  private listeners: ExecutorListener[] = [];

  constructor(deps: AgentExecutorDeps) {
    this.deps = deps;
  }

  /** 开始执行一轮（chat 返回后调用） */
  start(response: AgentV2ChatResponse): void {
    this.cancelCurrentTurn("new_turn");
    if (this.pumping) {
      // Do not reset the shared queue underneath the still-unwinding pump.
      // The cancelled run releases ownership in finally and starts this turn.
      this.pendingResponse = response;
      return;
    }
    this.beginTurn(response);
  }

  private beginTurn(response: AgentV2ChatResponse): void {
    this.sessionId = response.sessionId;
    this.turnId = response.turnId;
    this.planId = response.planId;
    this.abortController = new AbortController();
    this.deps.queue.enqueue(response.uiCommands || []);
    void this.pump();
  }

  /** 取消当前 turn 的未完成命令（新请求到来时调用） */
  cancelCurrentTurn(reason: string): void {
    this.abortController?.abort();
    this.deps.queue.cancel(reason);
  }

  getActiveTurn(): { sessionId: string; turnId: string; planId: string } {
    return { sessionId: this.sessionId, turnId: this.turnId, planId: this.planId };
  }

  onEvent(listener: ExecutorListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index >= 0) this.listeners.splice(index, 1);
    };
  }

  private async pump(): Promise<void> {
    if (this.pumping) return;
    this.pumping = true;
    const sessionId = this.sessionId;
    const turnId = this.turnId;
    const planId = this.planId;
    try {
      while (!this.deps.queue.isCancelled()) {
        try {
          const shouldContinue = await this.pumpOnce();
          if (!shouldContinue) break;
        } catch (error) {
          // 队列编排异常兜底：终止本轮，避免静默卡死
          this.emit({
            type: "planError",
            summary: error instanceof Error ? error.message : "命令队列执行异常"
          });
          break;
        }
      }
    } finally {
      this.pumping = false;
      const pending = this.pendingResponse;
      if (pending) {
        // A newer turn owns the UI now. Suppress the stale finish callback and
        // start it only after the previous pump has fully released the queue.
        this.pendingResponse = null;
        this.beginTurn(pending);
        return;
      }
      const planStatus = this.deps.queue.getPlanStatus();
      const patch = this.deps.queue.getAnswerPatch();
      this.emit({ type: "planFinished", planStatus, answerPatch: patch });
      this.deps.onTurnFinished?.({ sessionId, turnId, planId, planStatus, answerPatch: patch });
    }
  }

  /** 单步泵：执行一条命令，返回是否继续 */
  private async pumpOnce(): Promise<boolean> {
      if (this.abortController?.signal.aborted) return false;
      const item = this.deps.queue.getNextPending();
      if (!item) return false;
      const command = item.command;

      const ready = await this.waitPreconditions(command);
      if (!ready) {
        // A V4 command is part of a server-owned DAG. Silently skipping it
        // strands that DAG at its receipt/checkpoint boundary (for example,
        // the route action never releases its dependent route-story goal).
        // Submit a negative receipt even though dispatch never happened so
        // the backend can replan or terminate the goal visibly.
        const receipt: CommandReceipt = {
          status: "failed",
          errorCode: "COMMAND_PRECONDITION_FAILED",
          message: "命令未执行：页面或地图前置条件未满足",
          observedState: { commandNotExecuted: true, preconditionFailed: true },
          durationMs: 0
        };
        let report: CommandResultResponse;
        try {
          report = await this.reportCommand(command, receipt);
        } catch (error) {
          this.deps.queue.mark(item, "failed");
          const detail = error instanceof Error ? error.message : "失败回执提交失败";
          this.emit({
            type: "commandProgress",
            commandId: command.commandId,
            commandType: command.type,
            status: "failed",
            summary: `页面未就绪，且未能向后端提交失败回执：${detail}`
          });
          return false;
        }

        this.deps.queue.applyPatch({ answerPatch: report.answerPatch, planStatus: report.planStatus });
        if (report.nextCommands && report.nextCommands.length) {
          const retry = report.nextCommands.find((next) => isSameCommand(next, command));
          if (retry && this.deps.queue.resetToPending(item)) {
            this.emit({
              type: "commandRetry",
              commandId: command.commandId,
              commandType: command.type,
              summary: `页面就绪条件重试（第 ${item.attempts} 次）`
            });
            return true;
          }
          this.deps.queue.mark(item, "skipped");
          this.deps.queue.enqueueNext(report.nextCommands);
          this.emit({
            type: "commandDegraded",
            commandId: command.commandId,
            commandType: command.type,
            status: "skipped",
            summary: report.answerPatch || "页面未就绪，已按后端计划切换后续动作"
          });
          return true;
        }

        this.deps.queue.mark(item, "failed");
        this.emit({
          type: "commandProgress",
          commandId: command.commandId,
          commandType: command.type,
          status: "failed",
          summary: report.answerPatch || receipt.message
        });
        return true;
      }

      this.deps.queue.markRunning(item);
      this.emit({
        type: "commandProgress",
        commandId: command.commandId,
        commandType: command.type,
        status: "running",
        summary: `正在执行${this.summaryOf(command)}`
      });

      const receipt = await this.executeWithTimeout(command);
      if (this.deps.queue.isCancelled() || this.abortController?.signal.aborted) {
        this.deps.queue.mark(item, "cancelled");
        return false;
      }

      let report: CommandResultResponse;
      try {
        report = await this.reportCommand(command, receipt);
      } catch (error) {
        this.deps.queue.mark(item, "failed");
        const detail = error instanceof Error ? error.message : "页面状态回传失败";
        this.emit({
          type: "commandProgress",
          commandId: command.commandId,
          commandType: command.type,
          status: "failed",
          summary: `动作已执行，但验证回执未提交：${detail}`
        });
        return false;
      }

      if (receipt.status === "success") {
        const backendRejected = report.planStatus === "rejected"
          || report.planStatus === "unknown"
          || report.planStatus === "failed"
          || report.planStatus === "blocked";
        if (backendRejected && report.nextCommands && report.nextCommands.length) {
          this.deps.queue.mark(item, "skipped");
          this.deps.queue.enqueueNext(report.nextCommands);
          this.deps.queue.applyPatch({ answerPatch: report.answerPatch, planStatus: report.planStatus });
          this.emit({
            type: "commandDegraded",
            commandId: command.commandId,
            commandType: command.type,
            status: "skipped",
            summary: "页面观察未通过验证，已按后端计划继续修正"
          });
          return true;
        }
        if (backendRejected) {
          this.deps.queue.mark(item, "failed");
          this.emit({
            type: "commandProgress",
            commandId: command.commandId,
            commandType: command.type,
            status: "failed",
            summary: report.answerPatch || "页面动作已执行，但后端未接受验证回执"
          });
          return false;
        }
        this.deps.queue.mark(item, "success");
        this.emit({
          type: "commandProgress",
          commandId: command.commandId,
          commandType: command.type,
          status: "success",
          summary: "执行成功"
        });
        if (report.nextCommands && report.nextCommands.length) {
          this.deps.queue.enqueueNext(report.nextCommands);
        }
        this.deps.queue.applyPatch({ answerPatch: report.answerPatch, planStatus: report.planStatus });
        return true;
      }

      // 失败/超时：优先遵循后端 Verifier 的裁决
      if (report.nextCommands && report.nextCommands.length) {
        const sameCommand = report.nextCommands.find((next) => isSameCommand(next, command));
        if (sameCommand && this.deps.queue.resetToPending(item)) {
          this.emit({
            type: "commandRetry",
            commandId: command.commandId,
            commandType: command.type,
            summary: `重试（第 ${item.attempts} 次）`
          });
          return true;
        }
        // 降级/替换命令
        this.deps.queue.mark(item, "skipped");
        this.deps.queue.enqueueNext(report.nextCommands);
        this.emit({
          type: "commandDegraded",
          commandId: command.commandId,
          commandType: command.type,
          summary: "已降级执行替代命令"
        });
        return true;
      }

      // 本地兜底：failurePolicy=retry → 尝试重试
      if (command.failurePolicy === "retry" && this.deps.queue.resetToPending(item)) {
        this.emit({
          type: "commandRetry",
          commandId: command.commandId,
          commandType: command.type,
          summary: `重试（第 ${item.attempts} 次）`
        });
        return true;
      }

      this.deps.queue.mark(item, receipt.status === "timeout" ? "timeout" : "failed");
      this.emit({
        type: "commandProgress",
        commandId: command.commandId,
        commandType: command.type,
        status: receipt.status,
        summary: receipt.message || (receipt.status === "timeout" ? "执行超时" : "执行失败")
      });
      if (command.failurePolicy === "block") {
        return false;
      }
      return true;
  }

  private async waitPreconditions(command: AgentUiCommand): Promise<boolean> {
    const preconditions = command.preconditions || [];
    for (const precondition of preconditions) {
      switch (precondition.kind) {
        case "pageReady":
        case "mapReady": {
          // 纯路由命令（app.navigate/archive.*/contribution.*/analysis.*）由 router 自行
          // 处理页面切换，不依赖目标页注册能力——直接放行，避免等待 12s 超时后跳过。
          if (isPureNavigationCommand(command.type)) continue;
          const pageId = precondition.kind === "mapReady" ? "map" : precondition.ref;
          if (!pageId) continue;
          const capability = await this.deps.registry.whenReady(pageId, PRECONDITION_TIMEOUT_MS);
          if (!capability) {
            this.emit({
              type: "commandProgress",
              commandId: command.commandId,
              commandType: command.type,
              status: "waiting",
              summary: `页面 ${pageId} 未就绪`
            });
            return false;
          }
          break;
        }
        case "capabilityRegistered": {
          const pageId = precondition.ref;
          if (!pageId) break;
          if (isPureNavigationCommand(command.type)) break;
          const capability = await this.deps.registry.whenReady(pageId, PRECONDITION_TIMEOUT_MS);
          if (!capability) return false;
          break;
        }
        case "buildingLoaded": {
          // 建筑已加载以地图能力就绪为信号（serviceState online）
          const capability = await this.deps.registry.whenReady("map", PRECONDITION_TIMEOUT_MS);
          if (!capability) return false;
          break;
        }
        case "commandSuccess": {
          if (precondition.ref) {
            const state = this.deps.queue.getState(precondition.ref);
            if (state !== "success") return false;
          }
          break;
        }
        default:
          break;
      }
    }
    return true;
  }

  private async executeWithTimeout(command: AgentUiCommand): Promise<CommandReceipt> {
    const baseTimeout = command.timeoutMs ?? AgentConstants.DEFAULT_COMMAND_TIMEOUT_MS;
    // route/tour 分析是重管线，后端下发的 30s 超时不够，超时会误杀仍在正常计算的路线、
    // 让 agent 重试并堆积后台分析。前端对这类命令放宽到 3 分钟（覆盖后端下发的 30s）。
    const isRouteCommand = command.type.startsWith("tour.") || command.type === "map.renderRoute";
    const timeoutMs = isRouteCommand
      ? Math.max(baseTimeout, AgentConstants.ROUTE_COMMAND_SOFT_TIMEOUT_MS)
      : baseTimeout;
    const signal = this.abortController?.signal;
    let timer: number | undefined;
    const timeout = new Promise<CommandReceipt>((resolve) => {
      timer = setTimeout(() => {
        resolve({
          status: "timeout",
          errorCode: "COMMAND_TIMEOUT",
          message: `命令执行超时（${timeoutMs}ms）`
        });
      }, timeoutMs);
    });
    try {
      try {
        const receipt = await Promise.race([
          this.deps.dispatch(command, signal as AbortSignal),
          timeout
        ]);
        return receipt;
      } catch (error) {
        // 单条命令派发异常不终止整轮队列
        return {
          status: "failed",
          errorCode: "COMMAND_EXECUTION_FAILED",
          message: error instanceof Error ? error.message : "命令派发失败"
        };
      }
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }

  private async reportCommand(
    command: AgentUiCommand,
    receipt: CommandReceipt
  ): Promise<CommandResultResponse> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= RECEIPT_REPORT_ATTEMPTS; attempt += 1) {
      try {
        return await this.deps.reporter.report(
          { sessionId: this.sessionId, turnId: this.turnId, planId: this.planId },
          command,
          receipt,
          this.abortController?.signal
        );
      } catch (error) {
        lastError = error;
        if (this.abortController?.signal.aborted) throw error;
      }
    }
    throw lastError instanceof Error ? lastError : new Error("页面状态回传失败");
  }

  private summaryOf(command: AgentUiCommand): string {
    const label = command.params?.buildingName || command.target || "";
    return `${command.type}${label ? `（${label}）` : ""}`;
  }

  private emit(event: QueueEvent): void {
    for (const listener of [...this.listeners]) {
      try {
        listener(event);
      } catch {
        // 忽略监听器异常
      }
    }
  }
}

/**
 * 后端把命令放回队列时用它判断"这是同一条命令的重试"还是"替换命令"。
 * 只比 id 会把替换当重试：V2 的降级计划用固定序号、后端重规划也可能复用 id，
 * 那样前端会把新命令丢掉、继续重试已被否决的旧命令，白白烧掉重试次数。
 */
function isSameCommand(next: AgentUiCommand, current: AgentUiCommand): boolean {
  return next.commandId === current.commandId && next.type === current.type;
}

/** 纯路由命令：由 router 自行处理页面切换，无需等待目标页注册能力 */
function isPureNavigationCommand(type: string): boolean {
  return type === "app.navigate"
    || type.startsWith("archive.")
    || type.startsWith("contribution.")
    || type.startsWith("analysis.");
}
